import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from './language-context';
import { LanguageToggle } from './language-toggle';
import { ScIcon } from './chrome-icons';
import { isCurrent, type ScMenu } from './chrome-nav';
import { surfaceUrl } from './surface-links';
import { useSignedInAccount } from './signed-in-account';

// The header's phone / tablet menu (Blog design export `.mnav`, ≤ 1040 px): an accordion
// under the bar — Offers, Booking and Resources expand to their links, Videos is a plain
// row, then Sign in (or the customer dashboard when signed in) and "Add your business —
// free". The section holding the current page starts expanded. The design had no
// language control in the header at all; a Language row with the EN / বাং switch closes
// the list so a Bangla reader need not scroll to the footer.
export function ChromeMobileMenu({
  id,
  open,
  menus,
  videosActive,
}: {
  id: string;
  open: boolean;
  menus: ScMenu[];
  videosActive: boolean;
}) {
  const { t } = useLanguage();
  const { pathname, search } = useLocation();
  const { user } = useSignedInAccount();

  return (
    <nav className="sc-mnav sc-in" id={id} hidden={!open} aria-label={t('nav.menu')}>
      {menus.slice(0, 2).map((m) => (
        <Section key={m.id} menu={m} pathname={pathname} search={search} />
      ))}
      <Link to="/videos" aria-current={videosActive ? 'page' : undefined}>
        <ScIcon id="sc-i-video" className="sc-ni" />
        {t('nav.videos')}
        <span className="sc-bdg sc-bdg--new">{t('nav.new_badge')}</span>
      </Link>
      {menus.slice(2).map((m) => (
        <Section key={m.id} menu={m} pathname={pathname} search={search} />
      ))}
      {user ? (
        <a href={surfaceUrl('app', '/') ?? '/'}>
          <ScIcon id="sc-i-user" className="sc-ni" />
          {t('sc.account')}
        </a>
      ) : (
        <Link to="/login">
          <ScIcon id="sc-i-user" className="sc-ni" />
          {t('nav.sign_in')}
        </Link>
      )}
      <div className="sc-mrow">
        <span>{t('sc.language')}</span>
        <LanguageToggle size="sm" />
      </div>
      <Link className="sc-mcta" to="/for-business">
        {t('nav.add_business_free')}
      </Link>
    </nav>
  );
}

function Section({ menu, pathname, search }: { menu: ScMenu; pathname: string; search: string }) {
  const items = menu.groups.flatMap((g) => g.items);
  return (
    <details open={menu.active || undefined}>
      <summary>
        <ScIcon id={menu.icon} className="sc-ni" />
        {menu.label}
        <ScIcon id="sc-i-chev" className="sc-chev" />
      </summary>
      {items.map((item) => (
        <Link key={item.to + item.label} to={item.to} aria-current={isCurrent(item.to, pathname, search) ? 'page' : undefined}>
          {item.label}
          {item.badge ? <span className="sc-bdg sc-bdg--new">{item.badge}</span> : null}
        </Link>
      ))}
    </details>
  );
}
